import React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowUpIcon } from 'lucide-react';
interface SendButtonProps {
  ready: boolean;
  onClick: () => void;
  size?: 'md' | 'sm';
}
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * Send — two clear states, one smooth change between them.
 *
 * Empty: a pale indigo disc with an indigo arrow. It reads as "waiting",
 * not as a broken, half-faded button.
 * Ready (something typed): the disc fills with the same living
 * indigo→purple drift as Ask AI, crossfading in over ~250ms, and the arrow
 * turns white. Hover lifts the arrow slightly; a press squeezes it.
 */
export function SendButton({
  ready,
  onClick,
  size = 'md'
}: SendButtonProps) {
  const reduce = useReducedMotion();
  return <motion.button type="button" onClick={onClick} disabled={!ready} aria-label="Send" whileHover={ready && !reduce ? 'hover' : undefined} whileTap={ready && !reduce ? {
    scale: 0.94
  } : undefined} transition={{
    duration: 0.14,
    ease: EASE
  }} className={`group/send relative isolate flex-shrink-0 ${size === 'sm' ? 'w-10 h-10 mb-0.5' : 'ml-2 w-12 h-12'} flex items-center justify-center rounded-full disabled:cursor-not-allowed transition-shadow duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-indigo/40 focus-visible:ring-offset-2 ${ready ? 'shadow-[0_6px_18px_-6px_rgba(109,40,217,0.55)] hover:shadow-[0_10px_24px_-8px_rgba(109,40,217,0.65)]' : 'shadow-none'}`} data-id="element-3447" data-mp="xfo1lbj84lu7">
      <span aria-hidden className="absolute inset-0 rounded-full overflow-hidden bg-indigo-50 ring-1 ring-inset ring-primary-indigo/10" data-id="element-3448" data-mp="2nfplkfxq988w">
        <span className={`ask-ai-drift absolute inset-0 bg-gradient-to-r from-primary-indigo via-ai-purple to-primary-indigo bg-[length:300%_100%] transition-opacity duration-300 ease-out ${ready ? 'opacity-100' : 'opacity-0'}`} data-id="element-3449" data-mp="2x50v23oj0exb" />
        <span className={`absolute inset-x-0 top-0 h-px bg-white/30 transition-opacity duration-300 ease-out ${ready ? 'opacity-100' : 'opacity-0'}`} data-id="element-3450" data-mp="24neztyv7fqy0" />
      </span>
      <motion.span className="relative flex" variants={{
      hover: {
        y: -2
      }
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} data-id="element-3451" data-mp="1rpp9lee1dwb0">
        <ArrowUpIcon className={`w-5 h-5 transition-colors duration-300 ease-out ${ready ? 'text-white' : 'text-primary-indigo/45'}`} strokeWidth={2.25} data-id="element-3452" data-mp="26fo9bll8pfsl" />
      </motion.span>
      <AnimatePresence data-id="element-3453" data-mp="1dqzdo22ye2o8">
        {ready && !reduce && <motion.span key="arrive" aria-hidden className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-ai-purple/40" initial={{
        opacity: 0.7,
        scale: 1
      }} animate={{
        opacity: 0,
        scale: 1.25
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-3454" data-mp="18rd0ttr1eofy" />}
      </AnimatePresence>
    </motion.button>;
}